import {
  DigitalisProductDetailsEntity,
  EBUMobileProductDetailsEntity,
  EPressProductDetailsEntity,
  InternetProductAvailableOptionsEntity,
  MobileProductAvailableOptionsEntity,
  MobileProductAvailableSettingsEntity,
  MobileProductAvailableSubscriptionsEntity,
  OptionDetailsEntity,
  TelephonyProductAvailableOptionsEntity,
  TelephonyProductDetailsEntity,
  TvProductAvailableOptionsEntity,
} from '../configs/entity.config';
import {Injectable, OnInit} from '@angular/core';
import { Observable } from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import { EntityManager, IEntity } from '@proximus/entity-manager';
import {
  DeepLinkOptionDetailsEntity,
  InternetProductDetailsEntity,
  MobileProductDetailsEntity,
  ProductsEntity,
  TvProductDetailsEntity,
} from '../configs/entity.config';
import {
  DeepLinkProductWrapper,
  Option,
  OptionStatus,
  processUserProductData,
  ProductTypes,
  UserProduct,
} from '@dch/products-business-logic';
import { BrandService } from './brand.service';
import {CustomerService, TranslationService} from '@proximus/ng-common';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {

  constructor(
    private brandService: BrandService,
    private translationService: TranslationService,
    private customerAccount: CustomerService,
  ) {
  }

  getProducts(payload: any): Observable<UserProduct> {
    return this.isPrepaid().pipe(
      switchMap(prepaid => EntityManager.getEntity(ProductsEntity, {
      includeOptions: true,
      includePromos: true,
      appBrand: payload?.appBrand,
      includePayingOptionsForFlsUr: this.isUrBuyingEnabled() && !prepaid,
      refreshCache: true,
    }).pipe(map((entity) => processUserProductData(entity.value)))));
  }

  getProductsDetails(payload: any) {
    const appBrand = this.brandService.getAppBrandValue();
    if (payload.productType === 'mobile') {
      if (!payload.isEBU) {
        return this.isPrepaid().pipe(
          switchMap(prepaid => EntityManager.getEntity(MobileProductDetailsEntity, {
          productType: payload.productType,
          serviceId: payload.serviceId,
          appBrand,
          includePayingOptionsForFlsUr:
            this.isUrBuyingEnabled() && payload.isNotFullControlBrand && !prepaid,
          includePayingSubscriptionsForFlsUr:
            this.isUrBuyingEnabled() && payload.isNotFullControlBrand && !prepaid,
        })));
      } else {
        return EntityManager.getEntity(EBUMobileProductDetailsEntity, {
          productType: payload.productType,
          serviceId: payload.serviceId,
          appBrand,
        });
      }
    } else if (payload.productType === ProductTypes.TV.toString()) {
      return EntityManager.getEntity(TvProductDetailsEntity, {
        serviceId: payload.serviceId,
        appBrand,
      });
    } else if (payload.productType === ProductTypes.INTERNET.toString()) {
      return EntityManager.getEntity(InternetProductDetailsEntity, {
        productType: payload.productType,
        serviceId: payload.serviceId,
        appBrand,
      });
    } else if (
      payload.productType.toLowerCase() ===
      ProductTypes.EPRESS.toString().toLowerCase()
    ) {
      return EntityManager.getEntity(EPressProductDetailsEntity, {
        serviceId: payload.serviceId,
        appBrand,
      });
    } else if (
      payload.productType.toLowerCase() ===
      ProductTypes.DIGITALIS.toString().toLowerCase()
    ) {
      return EntityManager.getEntity(DigitalisProductDetailsEntity, {
        serviceId: payload.serviceId,
        includeSelector: true,
        appBrand,
      });
    } else if (
      payload.productType.toLowerCase() ===
      ProductTypes.TELEPHONY.toString().toLowerCase()
    ) {
      return EntityManager.getEntity(TelephonyProductDetailsEntity, {
        serviceId: payload.serviceId,
        includeSelector: true,
        appBrand,
      });
    }
  }

  getEBUProductsDetails(payload: any) {
    const appBrand = this.brandService.getAppBrandValue();
    if (payload.productType === 'mobile') {
      return EntityManager.getEntity(EBUMobileProductDetailsEntity, {
        productType: payload.productType,
        serviceId: payload.serviceId,
        appBrand,
      });
    }
  }

  getProductAvailableOptions(payload: any) {
    const appBrand = this.brandService.getAppBrandValue();
    if (payload.productType === 'mobile') {
      return this.isPrepaid().pipe(
        switchMap(prepaid => EntityManager.getEntity(MobileProductAvailableOptionsEntity, {
        productType: payload.productType,
        serviceId: payload.serviceId,
        categories: payload.categories,
        appBrand,
        includePayingOptionsForFlsUr:
          this.isUrBuyingEnabled() && payload.isNotFullControlBrand && !prepaid,
      })));
    } else if (payload.productType === ProductTypes.TV.toString()) {
      return EntityManager.getEntity(TvProductAvailableOptionsEntity, {
        serviceId: payload.serviceId,
        appBrand,
      });
    } else if (payload.productType === ProductTypes.INTERNET.toString()) {
      return EntityManager.getEntity(InternetProductAvailableOptionsEntity, {
        productType: payload.productType,
        serviceId: payload.serviceId,
        appBrand,
      });
    } else if (payload.productType === ProductTypes.TELEPHONY.toString()) {
      return EntityManager.getEntity(TelephonyProductAvailableOptionsEntity, {
        productType: payload.productType,
        serviceId: payload.serviceId,
        appBrand,
      });
    } else {
      throw new Error(
        'product type: ' + payload.productType + ' not supported.',
      );
    }
  }

  getProductAvailableSettings(payload: any) {
    const appBrand = this.brandService.getAppBrandValue();
    if (payload.productType === 'mobile') {
      return this.isPrepaid().pipe(
        switchMap(prepaid => EntityManager.getEntity(MobileProductAvailableSettingsEntity, {
        productType: payload.productType,
        serviceId: payload.serviceId,
        appBrand,
        includePayingOptionsForFlsUr:
          this.isUrBuyingEnabled() && payload.isNotFullControlBrand && !prepaid,
      })));
    } else {
      throw new Error(
        'product type: ' + payload.productType + ' not supported.',
      );
    }
  }

  getProductAvailableSubscriptions(payload: any) {
    const appBrand = this.brandService.getAppBrandValue();
    if (payload.productType === 'mobile') {
      return this.isPrepaid().pipe(
        switchMap(prepaid => {
          if (prepaid) {
            return null
          }
            return EntityManager.getEntity(
              MobileProductAvailableSubscriptionsEntity,
              {
                productType: payload.productType,
                serviceId: payload.serviceId,
                appBrand,
                includePayingSubscriptionsForFlsUr:
                  this.isUrBuyingEnabled() && payload.isNotFullControlBrand && !prepaid,
              },
            )
          }
        ));
    } else {
      throw new Error(
        'product type: ' + payload.productType + ' not supported.',
      );
    }
  }

  getDeepLinkOptionDetails(
    payload: any,
  ): Observable<IEntity<DeepLinkProductWrapper>> {
    const appBrand = this.brandService.getAppBrandValue();
    return EntityManager.getEntity(DeepLinkOptionDetailsEntity, {
      mpcUuid: payload,
      appBrand,
    });
  }

  getOptionDetails(payload: any): Observable<IEntity<DeepLinkProductWrapper>> {
    const appBrand = this.brandService.getAppBrandValue();
    return EntityManager.getEntity(OptionDetailsEntity, {
      optionId: payload.optionId,
      appBrand,
      customerId: payload.customerId,
    });
  }

  updateEBUOptionActiveStatus(payload: any) {
    if (payload.options && payload.optionId) {
      const { options, optionId } = payload;
      return options.map((option) => {
        if (option.optionCodeForDescription === optionId) {
          option.ibStatus =
            option.ibStatus === OptionStatus.ACTIVE
              ? OptionStatus.AVAILABLE
              : OptionStatus.ACTIVE;
          options.forEach((activeOption: Option) => {
            const optionToCease: any = option.incompatibleWith?.find(
              (incompatibleOption: any) =>
                incompatibleOption.pniCode ===
                activeOption.optionCodeForDescription,
            );
            if (optionToCease) {
              optionToCease.ibStatus = OptionStatus.AVAILABLE;
            }
          });
        } else {
          option.ibStatus = OptionStatus.AVAILABLE;
        }
        return option;
      });
    } else {
      throw new Error('Cannot update ebu ordering options status');
    }
  }

  private isUrBuyingEnabled() {
    return this.translationService.getTranslatedValueFor(
      'mobile.urbuying.enabled',
    ) === 'true'
      ? true
      : undefined;
  }

  private isPrepaid(): Observable<boolean> {
    return this.customerAccount.getCustomerAccount().pipe(
      map(customerAccount => customerAccount.isPrepaid()));
  }
}
